'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useState } from 'react';
import { CompetitionAboutSheet } from '@/components/social/competition/competition-about-sheet';
import { CompetitionDetailError, CompetitionDetailLoading, CompetitionDetailNotFound } from '@/components/social/competition/competition-boundaries';
import { CompetitionHero } from '@/components/social/competition/competition-hero';
import { CompetitionRankBar } from '@/components/social/competition/competition-rank-bar';
import { CompetitionStandings, type CompetitionStandingsStatus } from '@/components/social/competition/competition-standings';
import { CompetitionsGate } from '@/components/social/competition/competitions-gate';
import { PayoutWalletSheet } from '@/components/social/competition/payout-wallet-sheet';
import type { CompetitionViewerStanding, PayoutWalletState, TradingCompetitionStandingRow, TradingCompetitionSummary } from '@/components/social/competition/types';
import { useAppRouter } from '@/lib/network/use-app-router';
export type CompetitionDetailStatus = 'ready' | 'loading' | 'error' | 'not-found';
export interface CompetitionStandingsDisplay {
  status: CompetitionStandingsStatus;
  rows: readonly TradingCompetitionStandingRow[];
  /** Timestamp of the last successful standings read — restarts the poll ring. */
  updatedAtMs: number;
  /** More rows exist behind the loaded page. */
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}
export interface CompetitionViewerDisplay {
  /** The signed-in viewer's id as it appears in standings rows, or null when unknown. */
  userId: string | null;
  standing: CompetitionViewerStanding;
  payoutWallet: PayoutWalletState;
}
export interface CompetitionDetailPageProps {
  status: CompetitionDetailStatus;
  competition: TradingCompetitionSummary;
  initialNowMs: number | null;
  timeZone: string;
  standings: CompetitionStandingsDisplay;
  viewer: CompetitionViewerDisplay;
  /**
   * Persists the viewer's external payout wallet; the data owner runs the
   * mutation and calls `onSaved` only once the address is accepted, which is
   * what closes the sheet — a rejected address keeps it open on its error.
   */
  onSaveWallet: (address: string, onSaved: () => void) => void;
  /** Closing the sheet clears a failed save, so a rejection cannot outlive it. */
  onWalletSheetClosed: () => void;
}
const BACK_HREF = '/social';
export function CompetitionDetailPage(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CompetitionDetailLoading />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <CompetitionsGate loading={t0}><CompetitionDetailContent {...props} /></CompetitionsGate>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function CompetitionDetailContent(props) {
  const $ = _c(6);
  const router = useAppRouter();
  if (props.status === "loading") {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <CompetitionDetailLoading />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (props.status === "error") {
    let t0;
    if ($[1] !== router) {
      t0 = <CompetitionDetailError onRetry={() => router.refresh()} backHref={BACK_HREF} />;
      $[1] = router;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  if (props.status === "not-found") {
    let t0;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <CompetitionDetailNotFound backHref={BACK_HREF} />;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    return t0;
  }
  let t0;
  if ($[4] !== props) {
    t0 = <CompetitionDetailReady {...props} />;
    $[4] = props;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
}
function CompetitionDetailReady(props) {
  const $ = _c(46);
  const router = useAppRouter();
  const [aboutOpen, setAboutOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [pulseTick, setPulseTick] = useState(0);
  let t0;
  if ($[0] !== props.standings.rows || $[1] !== props.viewer.userId) {
    t0 = props.viewer.userId !== null && props.standings.rows.some(row => row.user.userId === props.viewer.userId);
    $[0] = props.standings.rows;
    $[1] = props.viewer.userId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const viewerRowLoaded = t0;
  const wallet = props.viewer.payoutWallet;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setAboutOpen(true);
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.competition || $[5] !== props.initialNowMs || $[6] !== props.timeZone) {
    t2 = <CompetitionHero competition={props.competition} size="full" initialNowMs={props.initialNowMs} timeZone={props.timeZone} onOpenInfo={t1} />;
    $[4] = props.competition;
    $[5] = props.initialNowMs;
    $[6] = props.timeZone;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== router) {
    t3 = () => router.refresh();
    $[8] = router;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setAboutOpen(true);
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props.competition || $[12] !== props.standings.hasMore || $[13] !== props.standings.loadingMore || $[14] !== props.standings.onLoadMore || $[15] !== props.standings.rows || $[16] !== props.standings.status || $[17] !== props.standings.updatedAtMs || $[18] !== props.timeZone || $[19] !== props.viewer.userId || $[20] !== pulseTick || $[21] !== t3) {
    t5 = <CompetitionStandings competition={props.competition} status={props.standings.status} rows={props.standings.rows} updatedAtMs={props.standings.updatedAtMs} viewerUserId={props.viewer.userId} pulseTick={pulseTick} hasMore={props.standings.hasMore} loadingMore={props.standings.loadingMore} onLoadMore={props.standings.onLoadMore} timeZone={props.timeZone} onRetry={t3} onOpenInfo={t4} />;
    $[11] = props.competition;
    $[12] = props.standings.hasMore;
    $[13] = props.standings.loadingMore;
    $[14] = props.standings.onLoadMore;
    $[15] = props.standings.rows;
    $[16] = props.standings.status;
    $[17] = props.standings.updatedAtMs;
    $[18] = props.timeZone;
    $[19] = props.viewer.userId;
    $[20] = pulseTick;
    $[21] = t3;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setWalletOpen(true);
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== viewerRowLoaded) {
    t7 = viewerRowLoaded ? {
      onJumpToMe: () => setPulseTick(_temp)
    } : {};
    $[24] = viewerRowLoaded;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== props.competition || $[27] !== props.initialNowMs || $[28] !== props.viewer.standing || $[29] !== t7 || $[30] !== wallet) {
    t8 = <CompetitionRankBar competition={props.competition} initialNowMs={props.initialNowMs} standing={props.viewer.standing} wallet={wallet} onAddWallet={t6} {...t7} />;
    $[26] = props.competition;
    $[27] = props.initialNowMs;
    $[28] = props.viewer.standing;
    $[29] = t7;
    $[30] = wallet;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== aboutOpen || $[33] !== props.competition || $[34] !== props.timeZone) {
    t9 = <CompetitionAboutSheet competition={props.competition} timeZone={props.timeZone} open={aboutOpen} onOpenChange={setAboutOpen} />;
    $[32] = aboutOpen;
    $[33] = props.competition;
    $[34] = props.timeZone;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== props || $[37] !== wallet || $[38] !== walletOpen) {
    t10 = walletOpen ? <PayoutWalletSheet open={true} onOpenChange={open => {
      setWalletOpen(open);
      if (!open) {
        props.onWalletSheetClosed();
      }
    }} competition={props.competition} currentAddress={wallet.address} saving={wallet.status === "saving"} onSave={address => props.onSaveWallet(address, () => setWalletOpen(false))} /> : null;
    $[36] = props;
    $[37] = wallet;
    $[38] = walletOpen;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t2 || $[42] !== t5 || $[43] !== t8 || $[44] !== t9) {
    t11 = <div className="flex min-w-0 w-full flex-col gap-6" data-testid="competition-detail-page">{t2}{t5}{t8}{t9}{t10}</div>;
    $[40] = t10;
    $[41] = t2;
    $[42] = t5;
    $[43] = t8;
    $[44] = t9;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
}
function _temp(tick) {
  return tick + 1;
}